import { useState } from 'react';
import { CheckCircle, DownloadSimple, Package, Tag, Truck } from 'shared/icons';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import { Mono } from '../../ui/Mono';
import { useToast } from '../../ui/Toast';
import { DownloadZplFile, SendZplToPrinter, useZplPrinters, ZplPrinterSelect } from '../zplPrinting';
import BalancePreview from './BalancePreview';
import { FormatQty, ParseRepackBalance } from './repackModel';

/**
 * O RECIBO da operação gravada — e, quando nasceu pallet, a ETIQUETA.
 *
 * §6.2 é literal: "só o Supervisor cria LPN… etiqueta impressa na hora". Um
 * pallet novo sem etiqueta colada é um pallet que ninguém acha, então a
 * impressão fica AQUI, no fim do fluxo, no mesmo seletor rede + USB (QZ Tray)
 * do M1/M3 — não existe um segundo caminho de impressão no sistema.
 *
 * O saldo aparece de novo (agora como recibo, não promessa): é o mesmo razão
 * que a prévia mostrou, devolvido pela gravação. Ver os dois lado a lado no
 * tempo é o que dá ao Supervisor a certeza de que a tela não mentiu.
 */
const RepackReceipt = ({ label, operation, result, onRestart }) => {
    const toast = useToast();
    const [sendingCode, setSendingCode] = useState(null);

    const createdCodes = Array.isArray(result?.created_lpn_codes) ? result.created_lpn_codes : [];
    const lpns = Array.isArray(result?.lpns) ? result.lpns : result?.lpn ? [result.lpn] : [];
    const createdLpns = lpns.filter((row) => createdCodes.includes(row.code));
    const balance = ParseRepackBalance(result);
    const volume = result?.shipment_volume ?? null;

    const { printers, localPrinters, selectedPrinter, setSelectedPrinter } = useZplPrinters(createdLpns.length > 0);

    const print = async (lpn) => {
        const target = selectedPrinter;
        if (!target || !lpn.zpl) {
            return;
        }
        setSendingCode(lpn.code);
        try {
            const printerName = await SendZplToPrinter({ target, printers, zpl: lpn.zpl, label });
            toast.success(`${label('wms.labels.sent')}${printerName ? ` — ${printerName}` : ''}`);
        } catch (error) {
            toast.error(error);
        } finally {
            setSendingCode(null);
        }
    };

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3 rounded-xl border-2 border-success-500 bg-success-soft px-4 py-3 dark:border-success-700 dark:bg-success-900/25">
                <CheckCircle size={28} className="shrink-0 text-success-700 dark:text-success-400" />
                <div className="min-w-0 flex-1">
                    <p className="text-base font-bold text-success-text dark:text-success-200">
                        {label(`wms.repack.done.${operation}`)}
                    </p>
                    <p className="text-xs text-success-text dark:text-success-300">{label('wms.repack.done_hint')}</p>
                </div>
                {result?.movement_kind ? (
                    <Badge variant="success">
                        {`${label('wms.repack.trail_kind')}: `}
                        <Mono className="ml-1">{result.movement_kind}</Mono>
                    </Badge>
                ) : null}
                <Button type="button" onClick={onRestart}>
                    {label('wms.repack.new_operation')}
                </Button>
            </div>

            {createdLpns.length > 0 ? (
                <section className="space-y-3 rounded-xl border border-mono-200 p-4 dark:border-mono-800">
                    <h3 className="text-sm font-bold text-black dark:text-white">
                        {label('wms.repack.created_lpns')}
                    </h3>
                    {createdLpns.map((lpn) => (
                        <div key={lpn.code} className="space-y-2 rounded-lg bg-mono-50 p-3 dark:bg-mono-900/50">
                            <div className="flex flex-wrap items-center gap-2">
                                <Package size={16} className="shrink-0 text-mono-500" />
                                <Mono className="text-lg font-bold text-black dark:text-white">{lpn.code}</Mono>
                                {lpn.location_code ? <Badge variant="neutral">{lpn.location_code}</Badge> : null}
                                {lpn.deposit_code ? <Badge variant="neutral">{lpn.deposit_code}</Badge> : null}
                                {lpn.is_bulky ? (
                                    <Badge variant="warning">{label('wms.lpn.field.is_bulky')}</Badge>
                                ) : null}
                            </div>
                            {lpn.zpl ? (
                                <div className="flex flex-wrap items-end gap-2">
                                    <div className="min-w-[220px] flex-1">
                                        <ZplPrinterSelect
                                            label={label}
                                            printers={printers}
                                            localPrinters={localPrinters}
                                            value={selectedPrinter}
                                            onChange={setSelectedPrinter}
                                            disabled={sendingCode === lpn.code}
                                        />
                                    </div>
                                    <Button
                                        type="button"
                                        onClick={() => print(lpn)}
                                        loading={sendingCode === lpn.code}
                                        disabled={!selectedPrinter}
                                    >
                                        <Tag size={16} className="mr-2" />
                                        {label('wms.labels.send')}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() => DownloadZplFile(lpn.zpl, `lpn-${lpn.code}.zpl`)}
                                    >
                                        <DownloadSimple size={16} className="mr-2" />
                                        {label('wms.labels.download')}
                                    </Button>
                                </div>
                            ) : (
                                <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.lpns.no_zpl')}</p>
                            )}
                        </div>
                    ))}
                </section>
            ) : null}

            {volume ? (
                <section className="space-y-2 rounded-xl border border-mono-200 p-4 dark:border-mono-800">
                    <h3 className="flex items-center gap-2 text-sm font-bold text-black dark:text-white">
                        <Truck size={16} className="text-mono-500" />
                        {label('wms.repack.volume_result')}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2">
                        <Mono className="text-lg font-bold text-black dark:text-white">{volume.code}</Mono>
                        <Badge variant={volume.status === 'OPEN' ? 'success' : 'neutral'}>{volume.status}</Badge>
                        <Mono className="text-sm text-mono-600 dark:text-mono-400">
                            {FormatQty(volume.total_qty ?? 0)}
                        </Mono>
                    </div>
                    {Array.isArray(volume.items) && volume.items.length > 0 ? (
                        <ul className="space-y-1">
                            {volume.items.map((item) => (
                                <li
                                    key={item.id}
                                    className="flex items-center justify-between gap-3 text-xs text-mono-600 dark:text-mono-400"
                                >
                                    <Mono className="min-w-0 flex-1 truncate">
                                        {item.sku || item.product_id}
                                        {item.supplier_lot ? ` · ${item.supplier_lot}` : ''}
                                    </Mono>
                                    <Mono className="shrink-0">{FormatQty(item.qty)}</Mono>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </section>
            ) : null}

            {balance ? <BalancePreview balance={balance} label={label} /> : null}
        </div>
    );
};

export default RepackReceipt;
